'use client';

import { useState } from 'react';
import { type BaseComponentProps, filterEntriesByType } from '@bedrock/core/contentful';
import { cn } from '@bedrock/ui/utils';
import {
  type ContentfulComposableElementType,
  type ContentfulInlineFrameType,
} from 'lib/contentful/types';
import { LazyWrap } from 'ui/common/lazy-wrap';

type FlipSnackProps = BaseComponentProps<ContentfulComposableElementType>;

function getLabel(frame: ContentfulInlineFrameType): string {
  return frame.title.replace(/flipsnack\s*/i, '').trim();
}

function FlipSnack({ fields }: Readonly<FlipSnackProps>) {
  const inlineFrames = filterEntriesByType<ContentfulInlineFrameType>(
    fields.elements,
    'inlineFrame',
  );
  const [activeIndex, setActiveIndex] = useState(0);
  const activeFrame = inlineFrames[activeIndex];

  if (!inlineFrames.length) return null;

  return (
    <section
      aria-label="FlipSnack menu"
      className="relative min-h-[700px] w-full bg-black py-[53px] md:py-0"
    >
      <div className="px-6 md:px-[5.5%]">
        <nav
          aria-label="Menu filter"
          className="flex w-full items-center justify-start gap-[19px] overflow-x-auto py-[var(--spacing-sm)] pl-[16px] md:mt-[54px] md:gap-[32px] md:pl-0 2xl:pl-[32px]"
        >
          {inlineFrames.map((frame, index) => (
            <button
              key={frame.sysId}
              aria-pressed={index === activeIndex}
              type="button"
              className={cn(
                'br-type-title-xl text-foreground min-h-6 shrink-0 border-b-2 border-transparent py-[var(--spacing-sm)] text-center transition-colors duration-200',
                'hover:text-[hsl(var(--colors-secondary))]',
                index === activeIndex &&
                  'border-[hsl(var(--colors-secondary))] text-[hsl(var(--colors-secondary))]',
              )}
              onClick={() => {
                setActiveIndex(index);
              }}
            >
              {getLabel(frame)}
            </button>
          ))}
        </nav>
      </div>

      {activeFrame ? (
        <LazyWrap className="relative z-10 m-auto flex w-full justify-center md:max-w-[768px] lg:max-w-[1024px]">
          <iframe
            allow="autoplay; clipboard-read; clipboard-write"
            allowFullScreen
            className="aspect-[0.74]"
            src={activeFrame.url}
            title={activeFrame.title}
            width="1024px"
          />
        </LazyWrap>
      ) : null}
    </section>
  );
}

export default FlipSnack;
